import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EnquiryForm from '@/components/EnquiryForm';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContact } from '@/context/ContactContext';

const ContactPage = () => {
  const { contactSettings } = useContact();

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: contactSettings.phone_primary,
      secondaryValue: contactSettings.phone_secondary,
      link: `tel:${contactSettings.phone_primary?.replace(/\s/g, '')}`,
    },
    {
      icon: Mail,
      label: 'Email',
      value: contactSettings.email_primary,
      secondaryValue: contactSettings.email_secondary,
      link: `mailto:${contactSettings.email_primary}`,
    },
    {
      icon: MapPin,
      label: 'Office',
      value: `${contactSettings.address_line1}, ${contactSettings.address_line2}`,
      link: contactSettings.google_maps_url || null,
    },
    {
      icon: Clock,
      label: 'Working Hours',
      value: contactSettings.working_hours_weekday,
      secondaryValue: contactSettings.working_hours_weekend,
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-r from-[#2a1e00] to-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#d4a828] text-xs uppercase tracking-[0.3em] mb-4 font-semibold">
            Get in Touch
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Ready to invest in Gurgaon&apos;s premium commercial real estate? Our team
            of experts is here to help you find the perfect property.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-[#fdf9f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2a1e00] mb-8">
                Let&apos;s Start a Conversation
              </h2>
              <p className="text-[#6a5a30] mb-12 leading-relaxed">
                Whether you&apos;re looking to invest, lease, or sell commercial
                property in Gurgaon, we&apos;re here to provide expert guidance every
                step of the way.
              </p>

              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                {contactInfo.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="bg-white p-6 hover:shadow-lg transition-shadow duration-300 rounded-xl border border-[rgba(201,168,76,0.2)]"
                    >
                      <div className="w-12 h-12 bg-[#c9a84c] flex items-center justify-center mb-4 rounded-lg">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-xs uppercase tracking-wider text-[#8a7a50] mb-2">
                        {item.label}
                      </p>
                      {item.link ? (
                        <a
                          href={item.link}
                          className="text-[#2a1e00] font-medium hover:text-[#c9a84c] transition-colors"
                          data-testid={`contact-${item.label.toLowerCase()}`}
                          target={item.label === 'Office' && item.link ? '_blank' : undefined}
                          rel={item.label === 'Office' && item.link ? 'noopener noreferrer' : undefined}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-[#2a1e00] font-medium">{item.value}</p>
                      )}
                      {item.secondaryValue && (
                        <p className="text-[#6a5a30] text-sm mt-1">{item.secondaryValue}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Quick Connect */}
              <div className="bg-[#2a1e00] p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-white mb-4">
                  Quick Connect
                </h3>
                <p className="text-white/60 mb-6">
                  Get instant response on WhatsApp or schedule a call with our team.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`https://wa.me/${contactSettings.whatsapp_number?.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button
                      className="w-full bg-[#25D366] hover:bg-[#20BD5C] text-white px-6 py-4 tracking-widest uppercase text-xs font-bold rounded-lg"
                      data-testid="whatsapp-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                  <a href={`tel:${contactSettings.phone_primary?.replace(/\s/g, '')}`} className="flex-1">
                    <Button
                      className="w-full btn-primary"
                      data-testid="call-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call Now
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Enquiry Form */}
            <div>
              <EnquiryForm />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c9a84c] text-xs uppercase tracking-[0.3em] mb-4 font-semibold">
              Why Choose Us
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2a1e00]">
              Your Trusted Real Estate Partner
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-[#fdf9f0] rounded-xl border border-[rgba(201,168,76,0.2)]">
              <div className="text-5xl font-bold text-[#c9a84c] mb-4">15+</div>
              <p className="text-[#2a1e00] font-bold mb-2">Years Experience</p>
              <p className="text-[#6a5a30] text-sm">
                Deep expertise in Gurgaon commercial real estate market.
              </p>
            </div>
            <div className="text-center p-8 bg-[#fdf9f0] rounded-xl border border-[rgba(201,168,76,0.2)]">
              <div className="text-5xl font-bold text-[#c9a84c] mb-4">500+</div>
              <p className="text-[#2a1e00] font-bold mb-2">Deals Closed</p>
              <p className="text-[#6a5a30] text-sm">
                Successfully completed transactions worth ₹500 Cr+.
              </p>
            </div>
            <div className="text-center p-8 bg-[#fdf9f0] rounded-xl border border-[rgba(201,168,76,0.2)]">
              <div className="text-5xl font-bold text-[#c9a84c] mb-4">100%</div>
              <p className="text-[#2a1e00] font-bold mb-2">Client Satisfaction</p>
              <p className="text-[#6a5a30] text-sm">
                Committed to exceeding client expectations.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
